import React from 'react';
import L from 'leaflet';

/**
 * Calculate distance in feet between two lat/lon points using the Haversine formula.
 */
const distanceInFeet = (lat1, lon1, lat2, lon2) => {
  const R = 20902231; // Earth's radius in feet
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Check if a vehicle's alwaysShow override is currently active (same logic as CampaignMap).
 */
const isAlwaysShowActiveForTray = (vehicle) => {
  if (!vehicle.alwaysShow || !vehicle.alwaysShow.enabled) return false;

  const { startTime, endTime, timezone } = vehicle.alwaysShow;
  if (!startTime || !endTime) return false;

  try {
    const params = new URLSearchParams(window.location.search);
    const spoofTime = params.get('spoofTime');

    let nowInTz;

    if (spoofTime) {
      nowInTz = new Date(spoofTime);
    } else {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone || 'America/Los_Angeles',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });

      const parts = formatter.formatToParts(now);
      const get = (type) => parts.find(p => p.type === type)?.value;
      nowInTz = new Date(`${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}`);
    }

    const start = new Date(startTime);
    const end = new Date(endTime);

    return nowInTz >= start && nowInTz <= end;
  } catch (e) {
    return false;
  }
};

const VehicleStatusDrawer = ({ vehicles, activeVehicles, loading, onVehicleClick, mapRef, bottomOffset = 0, maxHeight = 200 }) => {
  const getVehicleStatus = (vehicle) => {
    if (loading) return { status: 'Loading...', color: '#FFA500' };
    
    const activeVehicle = activeVehicles.find(v => v.id === vehicle.id);

    // Parade mode: alwaysShow is active and no trip ID on the active vehicle
    if (activeVehicle && !activeVehicle.tripId && vehicle.alwaysShow && isAlwaysShowActiveForTray(vehicle)) {
      const proximity = vehicle.alwaysShow.paradeProximity;
      if (proximity && activeVehicle.currentPosition) {
        const dist = distanceInFeet(
          activeVehicle.currentPosition[0],
          activeVehicle.currentPosition[1],
          proximity.lat,
          proximity.lon
        );
        if (dist <= proximity.radiusFeet) {
          // Within radius of staging point — not yet parading
          return {
            status: vehicle.alwaysShow.notParading || 'Staging',
            color: '#FFA500',
            isParadeMode: true
          };
        } else {
          // Away from staging point — parading!
          return {
            status: vehicle.alwaysShow.isParading || 'Parading!',
            color: '#2ECC71',
            isParadeMode: true
          };
        }
      }
      // Has alwaysShow but no proximity config — just show isParading
      return {
        status: vehicle.alwaysShow.isParading || 'In Service',
        color: '#2ECC71',
        isParadeMode: true
      };
    }

    if (activeVehicle) {
      return { 
        status: `In Service - Route ${activeVehicle.route?.short_name || activeVehicle.route?.id || activeVehicle.route?.name || 'Unknown'}`, 
        color: '#2ECC71' 
      };
    }
    
    return { 
      status: 'Out of Service', 
      color: '#E74C3C' 
    };
  };

  const handleVehicleClick = (vehicle) => {
    // Close all open modals/popups
    if (mapRef?.current) {
      mapRef.current.closePopup();
    }

    // Get vehicle position
    const activeVehicle = activeVehicles.find(v => v.id === vehicle.id);
    if (!activeVehicle || !activeVehicle.currentPosition) {
      return; // Vehicle not active or no position data
    }

    const vehiclePosition = activeVehicle.currentPosition;
    
    // Check if vehicle is within current map bounds
    if (mapRef?.current) {
      const map = mapRef.current;
      const bounds = map.getBounds();
      const vehicleLatLng = L.latLng(vehiclePosition[0], vehiclePosition[1]);
      
      if (!bounds.contains(vehicleLatLng)) {
        // Vehicle is not in view, zoom to show all routes
        // Get all route bounds and fit the map to show them
        const allActivePositions = activeVehicles
          .filter(v => v.currentPosition)
          .map(v => v.currentPosition);
        
        if (allActivePositions.length > 0) {
          const group = new L.featureGroup(
            allActivePositions.map(pos => L.marker(pos))
          );
          map.fitBounds(group.getBounds().pad(0.1));
        }
      }
    }

    // Open the modal for this vehicle (trigger popup)
    if (onVehicleClick) {
      onVehicleClick(vehicle, activeVehicle);
    }
  };

  return (
    <div id="vehicle-status-drawer" style={{
      position: 'fixed',
      bottom: typeof bottomOffset === 'number' ? `${bottomOffset}px` : bottomOffset,
      left: 0,
      right: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      borderTop: '1px solid #ddd',
      padding: '12px',
      zIndex: 1000,
      maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
      overflowY: 'auto'
    }}>
      <div style={{
        fontSize: '14px',
        fontWeight: 'bold',
        marginBottom: '8px',
        color: '#333'
      }}>
        Vehicle Status
      </div>
      
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '8px'
      }}>
        {vehicles
          .filter(v => v.active !== false)
          .sort((a, b) => {
            const statusA = getVehicleStatus(a);
            const statusB = getVehicleStatus(b);
            
            // In Service vehicles first (status contains "In Service")
            const aInService = statusA.status.includes('In Service');
            const bInService = statusB.status.includes('In Service');
            
            if (aInService && !bInService) return -1;
            if (!aInService && bInService) return 1;
            return 0; // Keep original order for same status
          })
          .map(vehicle => {
          const status = getVehicleStatus(vehicle);
          return (
            <div 
              key={vehicle.id} 
              onClick={() => handleVehicleClick(vehicle)}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 12px',
                backgroundColor: 'white',
                borderRadius: '6px',
                border: '1px solid #eee',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f8f9fa';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'white';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '2px'
              }}>
                <div style={{ fontWeight: 'bold', color: '#333' }}>
                  {vehicle.name}
                </div>
                {!status.isParadeMode && (
                  <div style={{ color: '#666', fontSize: '11px' }}>
                    Routes: {vehicle.routes.join(', ')}
                  </div>
                )}
              </div>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: status.color
                }}></div>
                <span style={{ 
                  color: status.color,
                  fontWeight: '500',
                  fontSize: '11px',
                  whiteSpace: 'nowrap'
                }}>
                  {status.status}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VehicleStatusDrawer;
